import { resolveCustomerOrderStatus, type CustomerOrderStatus } from '@order-up/common';

export interface TrackableOrder {
    orderState?: string;
    /** Canonical fulfillment type (dineIn | takeOut | delivery | quickService). */
    type?: string;
    /** Kitchen lifecycle: none | pending | sent | preparing | ready | served. */
    kitchenState?: string;
    /** ISO time a scheduled order is set for (set when orderState === 'scheduled'). */
    scheduledTime?: string;
    courierStatus?: string;
    courierStatusUpdatedAt?: string;
    trackingUrl?: string;
    courierName?: string;
    eta?: string;
    /** Read through `orderCourierLocation` — neutral first, legacy Drive name second. */
    courierLocation?: { lat: number; lng: number };
    dasherLocation?: { lat: number; lng: number };
    deliveryAddressComponents?: {
        line1?: string;
        line2?: string;
        city?: string;
        state?: string;
        postalCode?: string;
    };
}

/** Friendly "today at 6:00 PM" / "Mon, Jun 30 at 6:00 PM" for a scheduled time. */
function formatScheduled(iso: string | undefined): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    const sameDay = d.toDateString() === new Date().toDateString();
    if (sameDay) return `today at ${time}`;
    const date = d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
    return `${date} at ${time}`;
}

/**
 * Which step each guest status lights. The STATUS comes from the one guest-status
 * vocabulary in `@order-up/common` (`resolveCustomerOrderStatus`) — the same
 * function the order-status texts and the API's SMS template picker read — so the
 * tracker can never say "Being prepared" while the guest is texted "complete".
 * Only the presentation (which step, which subtitle) lives here.
 */
const STEP_OF_STATUS: Readonly<Record<CustomerOrderStatus, number>> = {
    awaitingConfirmation: 0,
    scheduled: 0,
    confirmed: 0,
    preparing: 1,
    ready: 2,
    outForDelivery: 2,
    completed: 3,
    // The cancelled banner is rendered separately; the timeline stays at the start.
    cancelled: 0,
};

/** Resolve the step list + active index from the shared guest status. */
export function resolveProgress(order: TrackableOrder): {
    steps: string[];
    activeIndex: number;
    activeSubtitle: string;
} {
    const isDelivery = order.type === 'delivery';
    const status = resolveCustomerOrderStatus(order);
    const kitchen = (order.kitchenState ?? '').toLowerCase();

    const steps = isDelivery
        ? ['Order received', 'Being prepared', 'Out for delivery', 'Delivered']
        : ['Order received', 'Being prepared', 'Ready for pickup', 'Completed'];

    // Acceptance lifecycle: an order awaiting the restaurant's decision, or
    // scheduled for later, has not been fired to the kitchen — tell the guest
    // the truth instead of an indefinite "Confirming…".
    if (status === null || status === 'awaitingConfirmation') {
        return {
            steps,
            activeIndex: 0,
            activeSubtitle: 'Waiting for the restaurant to confirm your order…',
        };
    }
    if (status === 'scheduled') {
        const when = formatScheduled(order.scheduledTime);
        return {
            steps,
            activeIndex: 0,
            activeSubtitle: when
                ? `Scheduled for ${when} — we’ll start it shortly before`
                : 'Scheduled — we’ll start it shortly before your time',
        };
    }

    // Accepted/paid but the kitchen hasn't been fired yet (the brief pre-fire
    // window, or an order awaiting staff to send it) — distinguish it from an
    // ambiguous "confirming" so a paid+accepted order never looks stuck.
    const notYetFired = kitchen === '' || kitchen === 'none' || kitchen === 'pending';
    const receivedSubtitle = notYetFired
        ? 'Order received — sending it to the kitchen…'
        : 'Confirming your order…';

    const activeIndex = STEP_OF_STATUS[status];
    const subtitles = isDelivery
        ? [receivedSubtitle, 'The kitchen is on it', 'Your driver is on the way', '']
        : [receivedSubtitle, 'The kitchen is on it', 'Come grab it — it’s ready!', ''];
    return { steps, activeIndex, activeSubtitle: subtitles[activeIndex] };
}
